import {SimpleListView} from '@github-ui/simple-list-view'
import {SimpleListItem} from '@github-ui/simple-list-view/SimpleListItem'
import styles from './TaskList.module.css'
import {getLeadingVisualIconAttributes} from '@github-ui/agent-sessions/utils/get-leading-visual-icon-attributes'
import type {IssueAgentTask, IssueAssignedAgentInfo} from '../contexts/IssueAgentTasksContext'
import type {SessionState} from '@github-ui/agent-sessions/types/session'
import type {ArtifactState} from '@github-ui/agent-sessions/types/artifact'
import {isSessionState} from '@github-ui/agent-sessions/types/session'
import {getGitHubArtifactData} from '@github-ui/agent-sessions/utils/task-artifact-helpers'
import {useIsVisualizationEnabled} from '../utils/is-visualization-enabled'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {RelativeTime, Truncate} from '@primer/react'
import {TASK_NOUN} from '../utils/constants'
import type {LinkedIssuePullRequest} from '../utils/types'
import {getTaskStateLabel} from '../utils/get-task-state-label'
import {GitPullRequestIcon} from '@primer/octicons-react'

type TaskProps = {
  assignedAgents: IssueAssignedAgentInfo[]
  linkedPullRequests: LinkedIssuePullRequest[]
  onSelectTask?: (task: IssueAgentTask) => void
}

type TaskListProps = TaskProps & {
  tasks: IssueAgentTask[]
}

type TaskListItemProps = TaskProps & {
  task: IssueAgentTask
}

export const TaskList = ({tasks, ...props}: TaskListProps) => {
  return (
    <SimpleListView className={styles.taskList}>
      <SimpleListView.Items>
        {tasks.map(task => (
          <TaskListItem key={task.id} task={task} {...props} />
        ))}
      </SimpleListView.Items>
    </SimpleListView>
  )
}

function TaskListItem({task, assignedAgents, linkedPullRequests, onSelectTask}: TaskListItemProps) {
  const sidePanelEnabled = useIsVisualizationEnabled()
  const taskState = task.state ?? task.status ?? 'in_progress'
  const state: SessionState = isSessionState(taskState) ? taskState : 'in_progress'
  const title = task.name || `New ${TASK_NOUN}`

  const artifactData = getGitHubArtifactData(task.artifacts)
  const pullRequestId = artifactData?.id
  const pullRequest = linkedPullRequests.find(linkedPr => linkedPr.databaseId === pullRequestId) ?? null
  const leadingVisualAttributes = getLeadingVisualIconAttributes(
    artifactData?.state ?? (pullRequest?.state?.toLowerCase() as ArtifactState | undefined),
    false,
    state,
    !!task.archived_at,
  )
  const agentInfo = assignedAgents.find(agent => task.agent_collaborators?.some(c => c.agent_id === agent.id))
  const taskUrl = task.url
  const updatedAt = task.updated_at ?? task.last_updated_at ?? task.created_at

  return (
    <SimpleListItem className={styles.item}>
      <SimpleListItem.LeadingVisual className={styles.itemLeadingVisual}>
        <div
          style={{
            color: leadingVisualAttributes.color,
            marginTop: state === 'in_progress' ? '3px' : '1px',
          }}
        >
          <leadingVisualAttributes.icon />
        </div>
      </SimpleListItem.LeadingVisual>

      {taskUrl ? (
        <SimpleListItem.Title
          className={styles.taskTitle}
          href={taskUrl}
          onClick={
            sidePanelEnabled
              ? (e: React.MouseEvent) => {
                  // Let modifier-clicks and middle-clicks open the link in a new tab natively
                  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return
                  e.preventDefault()
                  onSelectTask?.(task)
                }
              : undefined
          }
        >
          <Truncate title={title} maxWidth="100%">
            {title}
          </Truncate>
        </SimpleListItem.Title>
      ) : sidePanelEnabled ? (
        <SimpleListItem.Title className={styles.taskTitle} onClick={() => onSelectTask?.(task)}>
          <Truncate title={title} maxWidth="100%">
            {title}
          </Truncate>
        </SimpleListItem.Title>
      ) : (
        <SimpleListItem.Title className={styles.taskTitle}>
          <Truncate title={title} maxWidth="100%">
            {title}
          </Truncate>
        </SimpleListItem.Title>
      )}

      <SimpleListItem.Description className={styles.itemDescription}>
        <>
          {getTaskStateLabel(taskState, pullRequest)}
          {updatedAt && (
            <>
              {' · '}Updated <RelativeTime tense="past" datetime={updatedAt} format="relative" />
            </>
          )}
        </>
      </SimpleListItem.Description>
      <SimpleListItem.Actions className={styles.itemActions}>
        {pullRequest && (
          <a
            className={styles.prNumberButton}
            href={pullRequest.url}
            aria-label={`View pull request #${pullRequest.number}`}
          >
            <GitPullRequestIcon />#{pullRequest.number}
          </a>
        )}

        <div className={styles.agentAvatarTrailingVisual}>
          {agentInfo?.avatar_url && (
            <GitHubAvatar src={agentInfo?.avatar_url} size={16} square alt={agentInfo?.display_name || 'Agent'} />
          )}
        </div>
      </SimpleListItem.Actions>
    </SimpleListItem>
  )
}
